import { Button, Grid, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { cloneDeep as _cloneDeep, get as _get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { addAnotherPersonBtn } from '../../../Constants/Constants';
import ThirdPartyInfoCollectionComponent from './ThirdPartyInfoCollectionComponent';

interface ContactInfo {
  id: number;
  firstName?: string;
  lastName?: string;
  personInjured: boolean;
  phoneNumber?: string;
  phoneNumberValid: boolean;
  emailAddress?: string;
  emailAddressValid: boolean;
  vehicleInvolved: boolean;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

const blankContact: ContactInfo = {
  id: 1,
  firstName: '',
  lastName: '',
  personInjured: false,
  phoneNumber: '',
  phoneNumberValid: true,
  emailAddress: '',
  emailAddressValid: true,
  vehicleInvolved: true,
  vehicleYear: '',
  vehicleMake: '',
  vehicleModel: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
};

export default function ThirdPartyInfo(props: any) {
  const theme: Theme = useTheme();
  // Update parent state when there is a change in contact info
  const { handleThirdPartyContactChange } = props;
  const [thirdPartyContactInfo, setThirdPartyContactInfo] = useState(() => {
    const prefilledInfo = _get(props, 'prefilledThirdPartyContactInfo', null);
    return prefilledInfo !== null ? prefilledInfo : [_cloneDeep(blankContact)];
  });
  // Use useEffect to call the parent function whenever thirdPartyContactInfo changes
  useEffect(() => {
    if (handleThirdPartyContactChange) {
      handleThirdPartyContactChange(thirdPartyContactInfo);
    }
  }, [thirdPartyContactInfo, handleThirdPartyContactChange]);

  const contactInfoUpdateHandler = useCallback(
    (updatedContactInfo: ContactInfo) => {
      const localContacts = [...thirdPartyContactInfo];
      //Find index of specific object using findIndex method.
      const index = localContacts.findIndex((obj) => obj.id === updatedContactInfo.id);

      localContacts[index] = updatedContactInfo;
      setThirdPartyContactInfo(localContacts);
    },
    [thirdPartyContactInfo]
  );

  const deleteContactHandler = useCallback(
    (contactInfo: ContactInfo) => {
      const localContacts = [...thirdPartyContactInfo];
      const index = localContacts.indexOf(contactInfo, 0);
      if (index > -1) {
        localContacts.splice(index, 1);
      }
      setThirdPartyContactInfo(localContacts);
    },
    [thirdPartyContactInfo]
  );

  function handleAddContact() {
    const newContact = _cloneDeep(blankContact);
    if (thirdPartyContactInfo && thirdPartyContactInfo.length) {
      const localContacts = [...thirdPartyContactInfo];
      newContact.id = thirdPartyContactInfo.length + 1;
      localContacts.push(newContact);
      setThirdPartyContactInfo(localContacts);
    } else {
      setThirdPartyContactInfo([newContact]);
    }
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
        maxWidth: '1280px',
        minWidth: '310px',
        margin: '0 auto',
        marginTop: '1em',
      }}
    >
      <Grid item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          Please enter their contact information.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Typography
          sx={{
            marginTop: '1em',
            marginBottom: '1em',
          }}
        >
          We understand not all information may have been collected. Please fill out what you can.
        </Typography>
      </Grid>

      {!!thirdPartyContactInfo &&
        thirdPartyContactInfo.length > 0 &&
        thirdPartyContactInfo.map((contact: any, index: number) => (
          <ThirdPartyInfoCollectionComponent
            key={index}
            theme={theme}
            contact={contact}
            id={index}
            contactInfoUpdateHandler={contactInfoUpdateHandler}
            deleteContactHandler={deleteContactHandler}
          ></ThirdPartyInfoCollectionComponent>
        ))}

      <Grid item xs={12}>
        <hr
          style={{
            border: theme.custom.styles.ctaHrStyle,
            width: '100%',
            marginBottom: '1.5em',
          }}
        ></hr>
      </Grid>

      <Grid item xs={12}>
        <Button
          onClick={() => handleAddContact()}
          variant="outlined"
          sx={{
            ...theme.custom.styles.genericSecondaryButton,
            marginLeft: '0px',
            maxWidth: '45%',
          }}
        >
          {addAnotherPersonBtn}
        </Button>
      </Grid>
    </Grid>
  );
}
